import { forwardRef, ForwardedRef, useMemo } from 'react';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import { GradientIcon } from './GradientIcon';
import { useLinearGradient } from 'src/hooks/useLinearGradient';
import { SxProps } from '@mui/material/styles';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import { memo } from '../../util/memo';

export type GradientIconButtonProps = IconButtonProps & {
  IconComponent: OverridableComponent<SvgIconTypeMap>;
  gradientColor?: string;
  iconBaseFill?: string;
  sx?: SxProps;
  sxOuter?: SxProps;
};

/**
 *
 * This only supports gradients that are defined in the src/styles/colors.ts file of the theme.
 */
const GradientIconButtonUnmemoized = forwardRef(
  (props: GradientIconButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      IconComponent,
      gradientColor = 'primary.horizontal',
      iconBaseFill,
      size,
      sx,
      sxOuter,
      ...otherProps
    } = props;
    const gradientName = useLinearGradient(gradientColor);

    const iconBtn = useMemo(() => {
      const iconBtnSx = {
        '&:hover': { svg: { fill: `url(#${gradientName})` } },
        ...sxOuter,
      };
      if (!iconBaseFill) {
        iconBtnSx['svg'] = { fill: `url(#${gradientName})` };
      }
      return (
        <IconButton {...otherProps} ref={ref} sx={iconBtnSx}>
          <GradientIcon
            IconComponent={IconComponent}
            size={size}
            gradientColor={gradientColor}
            sx={{
              ...(iconBaseFill ? { fill: iconBaseFill } : {}),
              fontSize: 'inherit',
              ...sx,
            }}
          />
        </IconButton>
      );
    }, [
      IconComponent,
      gradientColor,
      gradientName,
      iconBaseFill,
      otherProps,
      ref,
      size,
      sx,
      sxOuter,
    ]);

    return iconBtn;
  },
);

export const GradientIconButton = memo(GradientIconButtonUnmemoized);
GradientIconButtonUnmemoized.displayName = 'GradientIconButtonUnmemoized';
